import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Clock, MessageCircle, CheckCircle2, Loader2 } from "lucide-react";
import { EMAIL, SERVICE_OPTIONS } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  nome: "",
  tipo: "Privato",
  email: "",
  telefono: "",
  citta: "",
  servizio: "",
  frequenza: "Una tantum",
  messaggio: "",
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-[#00B36B] focus:ring-4 focus:ring-[#00B36B]/10";

const ContactSection = ({ selectedService, onSelectService }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    if (selectedService) setForm((f) => ({ ...f, servizio: selectedService }));
  }, [selectedService]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (key === "servizio") onSelectService(value);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setTicket(data.ticket);
      const saved = JSON.parse(localStorage.getItem("clario_leads") || "[]");
      localStorage.setItem("clario_leads", JSON.stringify([...saved, data]));
      toast.success("Richiesta inviata con successo");
    } catch {
      toast.error(`Invio non riuscito. Riprova o scrivici a ${EMAIL}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="bg-white py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#009E5E]">
              Contatti
            </span>
            <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              Preventivo gratuito,{" "}
              <span className="font-serif-accent font-medium text-[#00B36B]">
                senza impegno
              </span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Compila il modulo: ti ricontattiamo entro 24 ore lavorative per fissare un
              sopralluogo gratuito, in presenza o in videochiamata.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 space-y-4">
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-[#F9FAFB] p-5 transition-colors hover:border-[#00B36B]/30"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00B36B]/10">
                  <Mail className="h-5 w-5 text-[#00B36B]" />
                </span>
                <span>
                  <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Email preventivi
                  </span>
                  <span className="block text-base font-bold text-slate-900">{EMAIL}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-[#F9FAFB] p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#00B36B]/10">
                  <Clock className="h-5 w-5 text-[#00B36B]" />
                </span>
                <span>
                  <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Tempi di risposta
                  </span>
                  <span className="block text-base font-bold text-slate-900">
                    Entro 24 ore lavorative
                  </span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="rounded-[1.75rem] border border-slate-100 bg-[#F9FAFB] p-6 shadow-[0_8px_40px_rgba(15,23,42,0.06)] sm:p-9">
            {ticket ? (
              <div data-testid="contact-success-message" className="flex flex-col items-center py-10 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#00B36B]/10">
                  <CheckCircle2 className="h-8 w-8 text-[#00B36B]" />
                </span>
                <h3 className="mt-6 text-2xl font-bold text-slate-900">Richiesta ricevuta</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                  Grazie per aver contattato Clario. Il tuo numero di riferimento è{" "}
                  <span className="font-bold text-[#009E5E]">{ticket}</span>: ti ricontatteremo
                  entro 24 ore lavorative.
                </p>
                <button
                  data-testid="contact-new-request-button"
                  onClick={() => {
                    setTicket(null);
                    setForm(EMPTY);
                  }}
                  className="mt-8 rounded-full border border-[#00B36B]/30 px-6 py-3 text-sm font-bold text-[#009E5E] transition-colors hover:bg-[#00B36B] hover:text-white"
                >
                  Invia un'altra richiesta
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Nome e Cognome *
                  </label>
                  <input
                    data-testid="contact-input-name"
                    required
                    minLength={2}
                    value={form.nome}
                    onChange={set("nome")}
                    placeholder="Mario Rossi"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Sei un
                  </label>
                  <select
                    data-testid="contact-select-type"
                    value={form.tipo}
                    onChange={set("tipo")}
                    className={inputCls}
                  >
                    <option>Privato</option>
                    <option>Azienda</option>
                    <option>Amministratore di condominio</option>
                    <option>Gestore studentato</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Email *
                  </label>
                  <input
                    data-testid="contact-input-email"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="mario.rossi@email.it"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Telefono *
                  </label>
                  <input
                    data-testid="contact-input-phone"
                    required
                    minLength={6}
                    type="tel"
                    value={form.telefono}
                    onChange={set("telefono")}
                    placeholder="+39 333 000 0000"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Città / Regione
                  </label>
                  <input
                    data-testid="contact-input-city"
                    value={form.citta}
                    onChange={set("citta")}
                    placeholder="Milano, Lombardia"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Servizio richiesto *
                  </label>
                  <select
                    data-testid="contact-select-service"
                    required
                    value={form.servizio}
                    onChange={set("servizio")}
                    className={inputCls}
                  >
                    <option value="" disabled>
                      Seleziona un servizio
                    </option>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Frequenza
                  </label>
                  <select
                    data-testid="contact-select-frequency"
                    value={form.frequenza}
                    onChange={set("frequenza")}
                    className={inputCls}
                  >
                    <option>Una tantum</option>
                    <option>Settimanale</option>
                    <option>Bisettimanale</option>
                    <option>Mensile</option>
                    <option>Altro</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Messaggio / Metratura stimata
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    rows={4}
                    value={form.messaggio}
                    onChange={set("messaggio")}
                    placeholder="Descrivi gli ambienti, la metratura indicativa e le tue esigenze..."
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <button
                    data-testid="contact-submit-button"
                    type="submit"
                    disabled={sending}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#00B36B] px-7 py-4 text-sm font-bold text-white shadow-[0_12px_32px_rgba(0,179,107,0.3)] transition-all duration-300 hover:bg-[#009E5E] hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto"
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <MessageCircle className="h-4 w-4" />
                    )}
                    {sending ? "Invio in corso..." : "Invia richiesta di preventivo"}
                  </button>
                  <p className="mt-3 text-xs text-slate-400">
                    Inviando accetti la nostra informativa privacy. Nessuno spam, solo il tuo
                    preventivo.
                  </p>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactSection;
